import React, { useState, useMemo } from "react";
import "./PressKit.css";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { getPressKitData, getAvailableCategories } from "./pressKitConfig";

const DigitalPresskit = () => {
  const [isBioCopied, setIsBioCopied] = useState(false);
  const [copiedText, setCopiedText] = useState("");
  const [currentBioIndex, setCurrentBioIndex] = useState(2);
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All"); // New: category filter

  // Get dynamic data from configuration
  const { logos, pressImages, bios, contacts, metadata, videos, heroVideo, factSheet, pageTitle, pageSubtitle } = useMemo(
    () => getPressKitData(),
    []
  );
  const availableCategories = useMemo(
    () => ["All", ...getAvailableCategories()],
    []
  );

  const bioLevels = bios;

  // Filter images based on selected category
  const filteredPressImages = useMemo(() => {
    return selectedCategory === "All"
      ? pressImages
      : pressImages.filter((image) => image.category === selectedCategory);
  }, [pressImages, selectedCategory]);

  // Dynamic preview images based on bio length
  const numPreviewImages = currentBioIndex < 3 ? 4 : 6;
  const previewImages = filteredPressImages.slice(0, numPreviewImages);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(label);
      setTimeout(() => setCopiedText(""), 2000);
    });
  };

  const copyBioToClipboard = () => {
    if (isBioCopied) return;
    navigator.clipboard.writeText(currentBio[currentLanguage]).then(() => {
      setIsBioCopied(true);
      setTimeout(() => setIsBioCopied(false), 2000);
    });
  };

  const downloadImage = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Updated download all function using dynamic data
  const handleDownloadAll = async () => {
    if (isZipping) return;

    setIsZipping(true);
    const zip = new JSZip();

    try {
      // Create folders using metadata
      const imgFolder = zip.folder(metadata.imagesFolderName);
      const logoFolder = zip.folder(metadata.logoFolderName);

      // Add all press images
      const imagePromises = pressImages.map(async (image) => {
        try {
          const response = await fetch(image.downloadUrl);
          if (!response.ok)
            throw new Error(`Failed to fetch ${image.filename}`);
          const blob = await response.blob();
          imgFolder.file(image.downloadFilename, blob);
        } catch (error) {
          console.warn(`Could not fetch image: ${image.filename}`, error);
        }
      });

      // Add all logos
      const logoPromises = logos.map(async (logo) => {
        try {
          const response = await fetch(logo.url);
          if (!response.ok) throw new Error(`Failed to fetch ${logo.filename}`);
          const blob = await response.blob();
          logoFolder.file(logo.downloadFilename, blob);
        } catch (error) {
          console.warn(`Could not fetch logo: ${logo.filename}`, error);
        }
      });

      await Promise.all([...imagePromises, ...logoPromises]);

      // Generate and download the zip file
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, metadata.zipFileName);
    } catch (error) {
      console.error("Error creating zip file:", error);
      alert(
        "Failed to create zip file. Please try again or download images individually."
      );
    } finally {
      setIsZipping(false);
    }
  };

  const nextBioLevel = () =>
    setCurrentBioIndex((prev) => Math.min(prev + 1, bioLevels.length - 1));
  const prevBioLevel = () =>
    setCurrentBioIndex((prev) => Math.max(prev - 1, 0));
  const currentBio = bioLevels[currentBioIndex];
  const bioLengthLabels = ["Shortest", "Short", "Medium", "Long", "Longest"];

  return (
    <div className="presskit-container">
      {/* Hero section with dynamic logos */}
      <div className="presskit-hero">
        <div className="hero-grid">
          <div className="hero-content">
            <h1 className="presskit-main-title">{pageTitle}</h1>
            <p className="presskit-main-subtitle">{pageSubtitle}</p>
          </div>
          <div className="hero-video-container hero-spacing">
            <div className="video-wrapper">
              <iframe
                src={heroVideo}
                title="The Creaks Hero Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      <div className="main-content">
        {/* Bio section (unchanged) */}
        <section className="bio-section">
          <div className="section-header">
            <h2 className="section-title">Band Biography</h2>
          </div>

          <div className="bio-controls">
            <div className="length-slider-container">
              <div className="slider-header">
                <label htmlFor="bio-length-slider">Adjust Bio Length</label>
                <span className="slider-current-label">
                  {bioLengthLabels[currentBioIndex]}
                </span>
              </div>
              <div className="slider-wrapper">
                <button
                  className="slider-btn"
                  onClick={prevBioLevel}
                  disabled={currentBioIndex === 0}
                  aria-label="Shorter bio"
                >
                  ‹
                </button>
                <input
                  type="range"
                  id="bio-length-slider"
                  min="0"
                  max={bioLevels.length - 1}
                  value={currentBioIndex}
                  onChange={(e) => setCurrentBioIndex(Number(e.target.value))}
                  className="bio-length-slider"
                />
                <button
                  className="slider-btn"
                  onClick={nextBioLevel}
                  disabled={currentBioIndex === bioLevels.length - 1}
                  aria-label="Longer bio"
                >
                  ›
                </button>
              </div>
            </div>
            <div className="language-toggle-container">
              <label className="language-label">Language</label>
              <div className="language-toggle">
                <button
                  className={`lang-btn ${currentLanguage === "en" ? "active" : ""
                    }`}
                  onClick={() => setCurrentLanguage("en")}
                >
                  EN
                </button>
                <button
                  className={`lang-btn ${currentLanguage === "cz" ? "active" : ""
                    }`}
                  onClick={() => setCurrentLanguage("cz")}
                >
                  CZ
                </button>
              </div>
            </div>
          </div>

          <div className="bio-content">
            <div
              className="bio-text-container"
              onClick={copyBioToClipboard}
              role="button"
              tabIndex="0"
            >
              <p className="bio-text">{currentBio[currentLanguage]}</p>
              <div className={`copy-overlay ${isBioCopied ? "copied" : ""}`}>
                <span className="copy-overlay-text">
                  {isBioCopied ? "✓ Copied!" : "Copy to Clipboard"}
                </span>
              </div>
              <button
                className={`mobile-action-button copy-button ${isBioCopied ? "copied" : ""
                  }`}
                aria-label="Copy bio"
                onClick={(e) => {
                  e.stopPropagation();
                  copyBioToClipboard();
                }}
              >
                <span>{isBioCopied ? "✓" : "📋"}</span>
              </button>
            </div>
            <div className="bio-tags">
              {currentBio.tags.map((tag, index) => (
                <span key={index} className="bio-tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>



        {/* Enhanced Gallery section with category filtering */}
        <section className="gallery-section">
          <div className="section-header">
            <h2 className="section-title">Press Images</h2>
            <div className="gallery-controls">
              {/* Category filter */}
              <button
                className="see-all-btn"
                onClick={() => setShowGalleryModal(true)}
              >
                See All Images ({filteredPressImages.length})
              </button>
            </div>
          </div>
          <div className="gallery-preview">
            {previewImages.map((image) => (
              <div
                key={image.id}
                className="gallery-preview-item"
                onClick={() => downloadImage(image.downloadUrl, image.downloadFilename)}
              >
                <div className="image-container">
                  <img
                    src={image.url}
                    alt={image.alt || image.downloadFilename}
                  />
                  <div className="image-overlay">
                    <span className="logo-name">{image.category}</span>
                    <span className="download-hint">Download</span>
                  </div>
                  <div className="mobile-action-button" aria-label="Download">
                    <span>↓</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Videos Section */}
      <section className="video-section">
        <div className="section-header">
          <h2 className="section-title">Music Videos</h2>
        </div>
        <div className="video-grid">
          {videos.map((video, index) => (
            <div key={index} className="video-item">
              <div className="video-wrapper">
                <iframe
                  src={video.embedUrl}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <h3 className="video-title">{video.title}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* Info Grid Section (Logos + Fact Sheet) */}
      <section className="info-grid-section">
        <div className="info-grid-container">
          {/* Left Column: Logos (Mosaic) */}
          <div className="logo-column">
            <div className="section-header small-header">
              <h2 className="section-title">Official Logos</h2>
            </div>
            <div className="logo-mosaic sidebar-mosaic">
              {logos.map((logo) => (
                <div
                  key={logo.id}
                  className={`logo-tile ${logo.size}`}
                  onClick={() => downloadImage(logo.url, logo.downloadFilename)}
                >
                  <img src={logo.url} alt={logo.name} />
                  <div className="logo-overlay">
                    <span className="logo-name">{logo.name}</span>
                    <span className="download-hint">Download</span>
                  </div>
                  <div className="mobile-action-button" aria-label="Download">
                    <span>↓</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Fact Sheet */}
          <div className="fact-sheet-column">
            <div className="section-header small-header">
              <h2 className="section-title">{factSheet[currentLanguage].title}</h2>
            </div>
            <div className="fact-sheet-card">
              {factSheet[currentLanguage].items.map((item, index) => (
                <div key={index} className="fact-item">
                  <span className="fact-label">{item.label}</span>
                  <span className="fact-value">
                    {item.value.split("\n").map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < item.value.split("\n").length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Contact section */}
      <section className="contact-section">
        <div className="section-header">
          <h2 className="section-title">Press Contact</h2>
        </div>
        <div className="contact-grid">
          <div className="contact-card">
            <h3>Press Inquiries</h3>
            <p>{contacts.press}</p>
            <button
              className={`copy-button ${copiedText === "press-email" ? "copied" : ""
                }`}
              onClick={() => copyToClipboard(contacts.press, "press-email")}
            >
              {copiedText === "press-email" ? "✓ Copied" : "Copy Email"}
            </button>
          </div>
          <div className="contact-card">
            <h3>Booking</h3>
            <p>{contacts.booking}</p>
            <button
              className={`copy-button ${copiedText === "booking-email" ? "copied" : ""
                }`}
              onClick={() => copyToClipboard(contacts.booking, "booking-email")}
            >
              {copiedText === "booking-email" ? "✓ Copied" : "Copy Email"}
            </button>
          </div>
        </div>
      </section>

      {/* Enhanced Gallery Modal */}
      {showGalleryModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowGalleryModal(false)}
        >
          <div className="gallery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>All Press Images</h2>
              <div className="modal-controls">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-filter"
                >
                  {availableCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <span className="image-count">
                  {filteredPressImages.length} images
                </span>
              </div>
              <div className="modal-actions">
                <button
                  className="download-all-btn"
                  onClick={handleDownloadAll}
                  disabled={isZipping}
                >
                  {isZipping ? "Zipping..." : "Download All"}
                </button>
                <button
                  className="close-modal-btn"
                  onClick={() => setShowGalleryModal(false)}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="modal-gallery">
              {filteredPressImages.map((image) => (
                <div
                  key={image.id}
                  className="modal-gallery-item"
                  onClick={() =>
                    downloadImage(image.downloadUrl, image.downloadFilename)
                  }
                >
                  <div className="modal-image-container">
                    <img
                      src={image.url}
                      alt={image.alt || image.downloadFilename}
                    />
                    <div className="modal-image-overlay">
                      <span className="logo-name">{image.category}</span>
                      <span className="download-hint">Download</span>
                    </div>
                    <div className="mobile-action-button" aria-label="Download">
                      <span>↓</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalPresskit;
